import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

/** Signal-alert settings + web-push subscription management. */
export function useAlertSettings(isAuthenticated, authHeaders) {
  const [alertSettings, setAlertSettings] = useState({
    in_app: true,
    push: false,
    alert_types: { strong_buy: true, strong_sell: true, entry_hit: true },
  });
  const [pushSupported, setPushSupported] = useState(false);

  useEffect(() => {
    if (isAuthenticated && 'serviceWorker' in navigator && 'PushManager' in window) {
      setPushSupported(true);
    }
  }, [isAuthenticated]);

  const fetchAlertSettings = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/signal-alerts/settings`, { headers });
      setAlertSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch alert settings:", error);
    }
  }, [authHeaders]);

  const updateAlertSettings = useCallback(async (newSettings) => {
    try {
      const response = await axios.post(`${API}/signal-alerts/settings`, newSettings, {
        headers: authHeaders(),
      });
      setAlertSettings(response.data);
      toast.success("Alert settings updated");
    } catch (error) {
      console.error("Failed to update alert settings:", error);
      toast.error("Failed to update settings");
    }
  }, [authHeaders]);

  const togglePushNotifications = useCallback(async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        toast.error("Push notifications not supported in this browser");
        return;
      }
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Notification permission denied");
        return;
      }
      const vapidRes = await axios.get(`${API}/signal-alerts/vapid-key`);
      const vapidKey = vapidRes.data.vapid_public_key;
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
      };
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      await axios.post(`${API}/signal-alerts/push-subscribe`,
        { subscription: subscription.toJSON() },
        { headers: authHeaders() }
      );
      updateAlertSettings({ ...alertSettings, push: true });
      toast.success("Push notifications enabled!");
    } catch (error) {
      console.error("Push setup error:", error);
      toast.error("Failed to enable push notifications");
    }
  }, [alertSettings, authHeaders, updateAlertSettings]);

  return { alertSettings, pushSupported, fetchAlertSettings, updateAlertSettings, togglePushNotifications };
}
